import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import {
  ChevronLeft,
  ChevronRight,
  Loader2,
  MessagesSquare,
  RefreshCw,
  Search,
  X,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { apiRequest } from '../services/apiClient';
import { fmtShortDateTime as formatTime } from '../utils/format';
import { intakeTypeLabel, IntakeStatusChip } from '../features/generate/intakeBits';
import { SkeletonTable } from '../components/Skeleton';
import { FilterSearchInput } from '../components/FormComponents';
import { normalizeSearchValue, readOptionParam, readSearchParam } from '../utils/urlFilters';
import useUrlSyncedFilters, { useReseedPageGuard } from '../hooks/useUrlSyncedFilters';

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'completed', label: 'Completed' },
  { value: 'abandoned', label: 'Abandoned' },
];

const TYPE_OPTIONS = [
  { value: '', label: 'All types' },
  { value: 'nda', label: 'NDA' },
  { value: 'grid', label: 'Grid' },
  { value: 'tprm', label: 'TPRM' },
];

const PAGE_SIZE_OPTIONS = [25, 50, 100];

// The ONE definition of "what this list's filters are, given a querystring".
// Used by the useState initializers at mount AND by the re-seed when the URL
// changes from outside (doc-195) — a second hand-written copy of these rules is
// how the two paths drift apart, taking doc-194's option-list validation and
// doc-168's trimmed-q rule with them.
function seedFromParams(params) {
  return {
    type: readOptionParam(params, 'type', TYPE_OPTIONS),
    status: readOptionParam(params, 'status', STATUS_OPTIONS),
    search: readSearchParam(params, 'q'),
    page: parseInt(params.get('page')) || 1,
  };
}

// Identity of "which filters are applied", for the page-reset guard. Built from
// the same field names seedFromParams returns, so the live-state call and the
// re-seed call cannot disagree about what counts as a filter change.
function filtersKeyOf({ type, status, search }) {
  return JSON.stringify([type, status, search]);
}

export default function AdminIntakes() {
  const { can, loading: authLoading } = useAuth();
  const canManageIntakes = can('manage_intakes');
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [intakes, setIntakes] = useState([]);
  // Filters and page seed FROM the URL so /admin/intakes?q=acme&page=2 is a
  // shareable deep link, and Back out of an intake restores the list you left.
  // Both selects validate against their own option list (doc-194): a value they
  // cannot display — a retired enum in an old bookmark — must not be applied
  // invisibly behind a select that still reads "All types".
  const [seed] = useState(() => seedFromParams(searchParams));
  const [type, setType] = useState(seed.type);
  const [status, setStatus] = useState(seed.status);
  const [search, setSearch] = useState(seed.search);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [page, setPage] = useState(seed.page);
  const [pageSize, setPageSize] = useState(() => {
    const stored = localStorage.getItem('intakesPageSize');
    return stored ? parseInt(stored) : 25;
  });
  const [pagination, setPagination] = useState({ total: 0, totalPages: 1 });
  // Bumped by the error callout's Retry button so the fetch effect re-runs with
  // identical filters — otherwise a failed load forces a hard refresh.
  const [retryToken, setRetryToken] = useState(0);

  // Reset to page 1 whenever a filter or the search term changes, so you can
  // never land on page 4 of a filter that only has one page — but not on mount,
  // or a deep-linked ?page=2 gets clobbered before the first fetch; and not when
  // the change is a re-seed landing, which carries its own page.
  const { markReseeded, isReseedArrival } = useReseedPageGuard();
  const filtersTouchedRef = useRef(false);
  useEffect(() => {
    if (!filtersTouchedRef.current) {
      filtersTouchedRef.current = true;
      return;
    }
    if (isReseedArrival(filtersKeyOf({ type, status, search }))) return;
    setPage(1);
  }, [type, status, search, isReseedArrival]);

  // Re-seed when the querystring changes from OUTSIDE this component — the
  // sidebar "Intakes" link while already on /admin/intakes, a CommandK jump to
  // this list. Without it the URL blanks and every control keeps its old value
  // (doc-195). Page is re-seeded too, so that click lands on an unfiltered
  // page 1 rather than page 2 of nothing.
  const markUrlWritten = useUrlSyncedFilters({
    searchParams,
    enabled: canManageIntakes,
    onExternalChange: useCallback((params) => {
      const next = seedFromParams(params);
      // Before the state updates, so the page-reset effect recognises the
      // filter change they cause and leaves next.page alone.
      markReseeded(filtersKeyOf(next));
      setType(next.type);
      setStatus(next.status);
      setSearch(next.search);
      setPage(next.page);
    }, [markReseeded]),
  });

  // Mirror the live filters back into the querystring. Empty values are DELETEd
  // rather than written blank, so clearing a filter leaves no stale ?q= behind.
  // Gated on the capability like the fetch effect below: this component renders
  // <Navigate to="/"> for anyone without it, but hooks run whatever render
  // returned — an unguarded write here replaces the URL straight back to
  // /admin/intakes after the redirect and dead-ends on a blank page. The
  // capability MUST stay in the deps too: it is false while auth loads, and the
  // other four do not change when it flips true.
  useEffect(() => {
    if (!canManageIntakes) return;
    const params = new URLSearchParams(searchParams);
    if (type) params.set('type', type); else params.delete('type');
    if (status) params.set('status', status); else params.delete('status');
    // The TRIMMED term, matching what the fetch below sends — writing the raw
    // value here is how ?q=++ ends up in the URL with nothing filtered.
    const q = normalizeSearchValue(search);
    if (q) params.set('q', q); else params.delete('q');
    if (page > 1) params.set('page', page.toString()); else params.delete('page');
    setSearchParams(params, { replace: true });
    // Record what WE wrote, so the re-seed effect above can tell this write
    // apart from an external one and never feeds it back into itself.
    markUrlWritten(params);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canManageIntakes, type, status, search, page]);

  useEffect(() => {
    if (!canManageIntakes) return undefined;
    const params = new URLSearchParams();
    if (type) params.set('type', type);
    if (status) params.set('status', status);
    const q = normalizeSearchValue(search);
    if (q) params.set('q', q);
    params.set('page', page.toString());
    params.set('pageSize', pageSize.toString());

    // Debounced (search fires per keystroke) + stale-response guarded — a
    // slower earlier response must not paint over the latest.
    let cancelled = false;
    setLoading(true);
    const timer = setTimeout(() => {
      apiRequest(`/admin/intakes?${params.toString()}`)
        .then((data) => {
          if (cancelled) return;
          setIntakes(data.intakes || []);
          setPagination(data.pagination || { total: 0, totalPages: 1 });
          // Now that ?page= comes from the URL it can be out of range, and the
          // server CLAMPS rather than 404s (session-service.js listAllSessions),
          // reporting the page it actually served. Adopt it, or a hand-edited
          // ?page=9 renders "9 / 1" and "201–7 of 7" over page-1 rows. Also
          // self-heals the querystring via the sync effect above.
          const servedPage = data.pagination?.page;
          if (servedPage && servedPage !== page) setPage(servedPage);
          setError('');
          setLoading(false);
        })
        .catch((err) => {
          if (cancelled) return;
          setError(err.message);
          setLoading(false);
        });
    }, 200);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [canManageIntakes, type, status, search, page, pageSize, retryToken]);

  if (authLoading) {
    return (
      <div className="canvas-inner" style={{ textAlign: 'center', padding: 80 }}>
        <Loader2 className="w-5 h-5 animate-spin" style={{ color: 'var(--icon-secondary)' }} />
      </div>
    );
  }
  if (!canManageIntakes) return <Navigate to="/" replace />;

  const labelFor = (opts, value) => opts.find((o) => o.value === value)?.label || value;
  const clearAllFilters = () => { setType(''); setStatus(''); setSearch(''); };
  const hasFilters = Boolean(type || status || search.trim());
  const activeFilters = [
    type && { key: 'type', label: `Type: ${labelFor(TYPE_OPTIONS, type)}`, onRemove: () => setType('') },
    status && { key: 'status', label: `Status: ${labelFor(STATUS_OPTIONS, status)}`, onRemove: () => setStatus('') },
    search.trim() && { key: 'q', label: `Search: ${search.trim()}`, onRemove: () => setSearch('') },
  ].filter(Boolean);

  const handlePageSizeChange = (n) => {
    setPageSize(n);
    localStorage.setItem('intakesPageSize', n.toString());
    setPage(1);
  };

  return (
    <div className="canvas-inner animate-fade-in">
      <div className="page-header">
        <div>
          <div className="page-eyebrow">Admin</div>
          <h1 className="page-title">Intakes</h1>
          <div className="page-subtitle">
            Every self-serve intake across all users — in progress, completed, or abandoned.
          </div>
        </div>
        <button
          className="btn outline sm"
          onClick={() => setRetryToken((token) => token + 1)}
          disabled={loading}
        >
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          Refresh
        </button>
      </div>

      {/* Search — own full-width row, matching Contracts/TPRM/Reviews. */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 10, alignItems: 'center' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <FilterSearchInput
            className="filter-search-wide"
            icon={Search}
            placeholder="Search prospect, owner, email…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-search-input
          />
        </div>
      </div>

      {/* Option keys are prefix + value + index (doc-339): each list leads with
          an empty-valued "All …" placeholder, so keying on the value alone means
          a second empty-valued entry collides with it and React silently drops
          one. */}
      <div className="filter-row natural" style={{ marginBottom: activeFilters.length ? 10 : 16 }}>
        <select className="filter-select" value={type} onChange={(e) => setType(e.target.value)}>
          {TYPE_OPTIONS.map((o, i) => (
            <option key={`type-${o.value || ''}-${i}`} value={o.value}>{o.label}</option>
          ))}
        </select>
        <select className="filter-select" value={status} onChange={(e) => setStatus(e.target.value)}>
          {STATUS_OPTIONS.map((o, i) => (
            <option key={`status-${o.value || ''}-${i}`} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>

      {activeFilters.length > 0 && (
        <div className="active-filters" style={{ marginBottom: 16 }}>
          {activeFilters.map((f) => (
            <span key={f.key} className="active-filter-chip">
              {f.label}
              <button type="button" onClick={f.onRemove} aria-label={`Remove ${f.label}`}>
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
          <button className="btn ghost xs" onClick={clearAllFilters}>
            <X className="w-3 h-3" />
            Clear all
          </button>
        </div>
      )}

      {loading ? (
        <SkeletonTable rows={6} />
      ) : error ? (
        <div className="callout red">
          <div className="callout-body">
            <strong>Error loading intakes.</strong> {error}
            <div style={{ marginTop: 10 }}>
              <button
                className="btn outline sm"
                onClick={() => setRetryToken((token) => token + 1)}
              >
                <RefreshCw className="w-3.5 h-3.5" />
                Retry
              </button>
            </div>
          </div>
        </div>
      ) : intakes.length === 0 ? (
        <div className="card">
          <div className="card-body" style={{ padding: '64px 24px', textAlign: 'center' }}>
            <div
              style={{
                width: 44, height: 44, borderRadius: 999,
                background: 'var(--surface-alpha-tertiary)',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                marginBottom: 12,
              }}
            >
              <MessagesSquare className="w-5 h-5" style={{ color: 'var(--icon-tertiary)' }} />
            </div>
            <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--text-primary)', marginBottom: 4 }}>
              {hasFilters ? 'No matching intakes' : 'No intakes yet'}
            </div>
            <div style={{ fontSize: 12.5, color: 'var(--text-tertiary)', marginBottom: 16 }}>
              {hasFilters
                ? 'Try clearing the filters.'
                : 'Intakes appear here once someone starts the self-serve intake flow, in Docket or from Slack.'}
            </div>
            {hasFilters && (
              <button className="btn outline sm" onClick={clearAllFilters}>
                <X className="w-3.5 h-3.5" />
                Clear
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="card">
          <div style={{ overflowX: 'auto' }}>
            <table className="tbl">
              <thead>
                <tr>
                  <th style={{ width: 70 }}>Type</th>
                  <th>Counterparty / prospect</th>
                  <th style={{ width: 120 }}>Status</th>
                  <th>Started by</th>
                  <th style={{ width: 150 }}>Updated</th>
                </tr>
              </thead>
              <tbody>
                {intakes.map((it) => (
                  <tr
                    key={it.id}
                    onClick={() => navigate(`/intake/sessions/${it.id}`)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        navigate(`/intake/sessions/${it.id}`);
                      }
                    }}
                    role="button"
                    tabIndex={0}
                    style={{ cursor: 'pointer' }}
                  >
                    <td><span className="chip gray sq">{intakeTypeLabel(it.typeKey)}</span></td>
                    <td>
                      <Link to={`/intake/sessions/${it.id}`} onClick={(e) => e.stopPropagation()}>{it.prospectName || 'Untitled intake'}</Link>
                      {it.createdVia === 'slack' && (
                        <span className="chip purple sq" style={{ marginLeft: 6 }}>Slack</span>
                      )}
                    </td>
                    <td><IntakeStatusChip status={it.status} /></td>
                    <td style={{ fontSize: 12.5 }}>{it.ownerName || it.ownerEmail || '—'}</td>
                    <td className="mono" style={{ fontSize: 11 }}>{formatTime(it.updatedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {pagination.total > 0 && (
            <div
              style={{
                padding: '10px 24px',
                borderTop: '0.5px solid var(--border-primary)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                fontSize: 12,
                color: 'var(--text-tertiary)',
              }}
            >
              <div className="row" style={{ gap: 16 }}>
                <span className="tnum">
                  {((page - 1) * pageSize) + 1}–{Math.min(page * pageSize, pagination.total)} of {pagination.total}
                </span>
                <div className="row" style={{ gap: 6 }}>
                  <span>Per page</span>
                  <select
                    className="filter-select"
                    value={pageSize}
                    onChange={(e) => handlePageSizeChange(parseInt(e.target.value))}
                    style={{ height: 24, padding: '0 22px 0 8px', fontSize: 11 }}
                  >
                    {PAGE_SIZE_OPTIONS.map((size) => (
                      <option key={size} value={size}>{size}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="row" style={{ gap: 4 }}>
                <button
                  className="icon-btn xs"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  aria-label="Previous page"
                  style={{ opacity: page === 1 ? 0.4 : 1 }}
                >
                  <ChevronLeft className="w-3.5 h-3.5" />
                </button>
                <span className="mono tnum" style={{ minWidth: 56, textAlign: 'center' }}>
                  {page} / {pagination.totalPages}
                </span>
                <button
                  className="icon-btn xs"
                  onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
                  disabled={page === pagination.totalPages}
                  aria-label="Next page"
                  style={{ opacity: page === pagination.totalPages ? 0.4 : 1 }}
                >
                  <ChevronRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
